import { useMemo } from "react";
import { InfoOutlined } from "@mui/icons-material";
import { Box, Card, IconButton, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { isNil, isObject, isArray, isString, size } from "lodash";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";

const ObjectInfoTooltip = ({
  data,
  title = "Meta Data Details",
  titleKey = null, // Optional: key to use as dynamic title
  excludeKeys = [], // Optional: keys to exclude from display (deprecated, use hide)
  formatters = {}, // Optional: custom formatters for specific keys (deprecated, use format)
  hide = [], // Array of field names to hide
  show = [], // Array of field names to show (if provided, only these will be shown)
  format = [], // Array of objects: [{ field: 'fieldName', type: 'dollar|percent|number|date' }]
  minWidth = "200px",
  maxWidth = "480px",
  returnEmptyChildWhenMissingData,
  placement = "top",
  children,
}) => {
  // Format key name for display (convert camelCase to Title Case)
  const formatKeyName = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/_/g, " ");
  };

  // Format specific field types
  const formatFieldType = (value, type) => {
    if (isNil(value)) return "N/A";

    const numValue = typeof value === "string" ? parseFloat(value) : value;

    switch (type) {
      case "dollar":
        if (isNaN(numValue)) return String(value);
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(numValue);

      case "percent":
        if (isNaN(numValue)) return String(value);
        return new Intl.NumberFormat("en-US", {
          style: "percent",
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(numValue / 100); // Assuming value is like 25 for 25%

      case "number":
        if (isNaN(numValue)) return String(value);
        return new Intl.NumberFormat("en-US").format(numValue);

      case "date":
        return formatDateTime(value);

      default:
        return String(value);
    }
  };

  // Format value based on type and key
  const formatValue = (key, value) => {
    if (isNil(value)) return "N/A";

    // Check for custom format specification
    const customFormat = format.find((f) => f.field === key);
    if (customFormat) {
      return formatFieldType(value, customFormat.type);
    }

    // Use legacy custom formatter if provided (for backward compatibility)
    if (formatters[key]) {
      return formatters[key](value);
    }

    // Default formatting
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "number") {
      return value.toLocaleString();
    }

    return String(value);
  };

  // Get filtered entries
  const displayEntries = useMemo(() => {
    if (!data || !isObject(data)) return [];

    return Object.entries(data)
      .filter(([key, value]) => {
        // If show array is provided, only show those fields
        if (show.length > 0) {
          return show.includes(key);
        }

        // Skip if key is in hide list
        if (hide.includes(key)) return false;

        // Skip if key is in legacy excludeKeys list (for backward compatibility)
        if (excludeKeys.includes(key)) return false;

        // Skip objects and arrays
        if (isObject(value) && !isString(value)) return false;
        if (isArray(value)) return false;

        return true;
      })
      .map(([key, value]) => ({
        key,
        displayKey: formatKeyName(key),
        value: formatValue(key, value),
      }));
  }, [data, hide, show, excludeKeys, format, formatters]);

  // Dynamic title
  const displayTitle = useMemo(() => {
    if (titleKey && data?.[titleKey]) {
      return String(data[titleKey]);
    }
    return title;
  }, [title, titleKey, data]);

  if (!data && returnEmptyChildWhenMissingData) {
    return children;
  }
  if (!data || displayEntries.length === 0) {
    return null;
  }

  const tooltipContent = (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        // gap: 1.2,
        p: 2,
        background: "#ffffff",
        borderRadius: 1.2,
        minWidth,
        maxWidth,
        ".txt": {
          fontSize: "10px",
          color: grey[600],
        },
      }}
    >
      <Txt
        bold
        sx={{
          fontSize: "12px !important",
          color: `${grey[700]} !important`,
          mb: 0.5,
          borderBottom: `1px solid ${grey[200]}`,
          pb: 1.5,
          lineHeight: 1.4,
        }}
      >
        {displayTitle}
      </Txt>

      {displayEntries.map(({ key, displayKey, value }, index) => (
        <Flx
          key={key}
          jb
          gap={3}
          sx={{
            borderBottom:
              index === size(displayEntries) - 1
                ? "none"
                : `1px solid ${grey[100]}`,
            py: 0.8,
            px: 0.3,
          }}
        >
          <Txt sx={{ whiteSpace: "nowrap" }}>{displayKey}</Txt>
          <Txt
            bold
            sx={{
              textAlign: "right",
              wordBreak: "break-word",
              maxWidth: "150px",
              lineHeight: 1.4,
            }}
          >
            {value}
          </Txt>
        </Flx>
      ))}
    </Card>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement={placement}
      arrow
      disableInteractive={false}
      componentsProps={{
        tooltip: {
          sx: {
            // backgroundColor: "transparent",
            // boxShadow: "none",
            p: 0,
            // p: "1px",
            // border: "none",
            // "& .MuiTooltip-arrow": {
            //   display: "none",
            // },
          },
        },
      }}
    >
      {children ? (
        <Box sx={{ flexGrow: 0, width: "max-content" }}>{children}</Box>
      ) : (
        <IconButton color="primary" size="small">
          <InfoOutlined color="primary" fontSize="small" />
        </IconButton>
      )}
    </Tooltip>
  );
};

export default ObjectInfoTooltip;
